/**
 * Item label settings — the DOMAIN VOCABULARY.
 *
 * `posSettings.itemLabelSettings` is the branch-wide configuration for item label stickers
 * (the small per-item labels a label printer produces, distinct from KOTs and bills). This
 * folder is the single place that names its fields and shapes; every other module in
 * `itemLabelSettings/` imports from here and nothing here imports from them.
 *
 * NO BEHAVIOUR LIVES IN THIS FILE — no redux, no platform API, no derivation. That is what
 * makes it safe for `rules/` (pure), `registry/` (declarative) and both platform variants of
 * `options/` to share it without any of them reaching for a bundler-resolved sibling.
 *
 * ⚠️ The wire field names are FIXED by the backend and several are misspelled there
 * (`itemLabelBarcodeDelimeter`, `printSeperateOptionLabels`). The enum KEYS below are the
 * corrected spellings; the enum VALUES are the wire truth. Always address a field through
 * `LabelSettingKey.X`, never a string literal, so a rename is one edit here.
 *
 * ⚠️ Regular `enum`s, deliberately NOT `const enum` — `packages/core` compiles with
 * `isolatedModules: true`, which cannot erase a `const enum` across files.
 */

/** Every writable field of `posSettings.itemLabelSettings`. Values are the wire names. */
export enum LabelSettingKey {
  /** Sale channels the labels print for. */
  Channels = "channelDefIds",
  /** Categories/subcategories that must NOT print a label. */
  ExcludedCategories = "excludeCategoryIds",
  /** Items that must NOT print a label. */
  ExcludedItems = "excludeItemIds",
  /** Which data variables appear on the label. */
  Template = "itemLabelTemplate",
  /** Physical label size; selects the print template downstream. */
  TemplateSize = "templateSize",
  /** Fields encoded into the barcode, when the template includes one. */
  BarcodeFields = "itemLabelBarcodeSpecialFields",
  /** Separator placed between barcode fields. */
  BarcodeDelimiter = "itemLabelBarcodeDelimeter",
  /** When online orders print: on accept, manually, or via KDS. */
  PrintStage = "printStage",
  /** Per-category -> printer routing groups. */
  Groups = "itemLabelGroups",
  /** Print one label per add-on, rather than one per item. */
  SeparateAddOnLabels = "printSeperateOptionLabels",
  /** Items whose add-ons are eligible for labels. */
  AddOnItems = "printOptionLabelItemIds",
  /** Split printing by add-on TYPE. */
  PrintByAddOnType = "enableLabelPrintingBasedOnAddOnType",
  /** Render customisations on one comma-separated line. */
  CommaSeparatedOptions = "commaSeparatedOptions",
  /** Default label printer. Stored as an array, consumed as `[0]`. */
  Printer = "printerId",
}

/** Fields of a single label group (the per-category routing rule). */
export enum LabelGroupKey {
  Name = "name",
  Categories = "categoryIds",
  Printers = "printerIds",
}

/** Separator written between barcode special fields. */
export enum BarcodeDelimiter {
  Hyphen = "-",
  Pipe = "|",
  Underscore = "_",
}

/** When a label prints for an ONLINE order. */
export enum PrintStage {
  OnAccept = "onAccept",
  Manual = "manual",
  UsingKds = "usingKDS",
}

/** Catalogue item kinds. `Group` items never get a label and are filtered out. */
export enum CatalogueItemType {
  Item = "Item",
  Variant = "Variant",
  Group = "Group",
}

/**
 * Values of the `itemLabelTemplate` picklist the app itself branches on. The full list is
 * server-driven; only these two carry logic on this side.
 */
export enum TemplateField {
  Name = "name",
  Barcode = "barcode",
}

/**
 * `listType` of the server master-data picklist backing a field's options.
 *
 * ⚠️ An ALIAS, not a new enum. `MASTER_DATA_PICKLIST_TYPES` already lives in
 * `core/enums/labelItemSettings`, where it types `BusinessSlice.getMasterDataPicklist`'s
 * payload. Declaring a second enum with the same three values here would be exactly the drift
 * "one definition, always" exists to prevent — and the audit's duplicate check matches on NAME,
 * so it would not have caught it. `PicklistType` is simply this domain's name for that enum.
 */
export { MASTER_DATA_PICKLIST_TYPES as PicklistType } from "../../enums/labelItemSettings";

/** How a field is presented and edited. Drives which control the renderer mounts. */
export enum ControlKind {
  /** Opens a picker; the value is a list of ids (or one id for a single-select). */
  Picker = "picker",
  /** A boolean on/off. */
  Toggle = "toggle",
  /** A closed set of choices rendered inline as chips. */
  Choice = "choice",
}

/** One per-category -> printer routing rule. */
export type ItemLabelGroup = {
  [LabelGroupKey.Name]: string;
  [LabelGroupKey.Categories]?: string[];
  [LabelGroupKey.Printers]: string[];
};

/**
 * The settings object as it travels over the wire.
 *
 * Every field is optional: a branch that has never opened this screen has `{}`, and the
 * backend omits rather than defaults. Readers must tolerate absence — which is why `rules/`
 * derives from the object instead of trusting it, and why the renderer reads through
 * `viewModel`.
 *
 * `Template` and `TemplateSize` are the two shapes worth noting: `Template` is an array but
 * arrives as `""` from older branches, and `TemplateSize` is a BARE STRING while every other
 * picker field stores an array.
 */
export type ItemLabelSettings = {
  [LabelSettingKey.Channels]?: string[];
  [LabelSettingKey.ExcludedCategories]?: string[];
  [LabelSettingKey.ExcludedItems]?: string[];
  [LabelSettingKey.Template]?: string[] | "";
  [LabelSettingKey.TemplateSize]?: string;
  [LabelSettingKey.BarcodeFields]?: string[];
  [LabelSettingKey.BarcodeDelimiter]?: BarcodeDelimiter | null;
  [LabelSettingKey.PrintStage]?: PrintStage;
  [LabelSettingKey.Groups]?: ItemLabelGroup[];
  [LabelSettingKey.SeparateAddOnLabels]?: boolean;
  [LabelSettingKey.AddOnItems]?: string[];
  [LabelSettingKey.PrintByAddOnType]?: boolean;
  [LabelSettingKey.CommaSeparatedOptions]?: boolean;
  [LabelSettingKey.Printer]?: string[];
};

/** The value a single field can hold. */
export type LabelSettingValue = ItemLabelSettings[keyof ItemLabelSettings];

/**
 * The whole `posSettings` document. We only ever WRITE `itemLabelSettings`, but the PUT is a
 * full-document replace, so the rest has to round-trip untouched — `version` above all.
 */
export type PosSettingsDocument = {
  itemLabelSettings?: ItemLabelSettings;
  version?: number;
  [key: string]: unknown;
};

/**
 * One selectable row in a picker, normalised away from its source shape.
 *
 * Sources disagree wildly — a picklist entry is `{ text, value }`, a category is
 * `{ name, mid }`, an item is `{ info: { longName }, mid }`, a printer is `{ label, mid }`.
 * `options/` maps all of them to this, so pickers and chip lists only ever speak one shape.
 */
export type Option = {
  /** The stored id. */
  value: string;
  /** What the user reads. */
  label: string;
  /** Secondary line (SKU, printer type). */
  caption?: string;
  /** Thumbnail source id, resolved to a URL by the renderer. */
  imageId?: string;
  /** Nested, VIEW-ONLY children — a category's subcategories. Not independently selectable. */
  children?: Option[];
  /** Extra strings the search box should match beyond `label`. */
  searchable?: string[];
  /** Picklist entries carry a per-size cap on how many template fields may be picked. */
  maxSelectable?: number;
};

/** A server master-data picklist entry, as returned by `masterdata/picklist/fetch`. */
export type PicklistEntry = {
  value: string;
  text: string;
  /** Only on label sizes: how many template variables that size has room for. */
  maxVariable?: number;
  subCategories?: PicklistEntry[];
};

/** A catalogue item as the option sources receive it. */
export type CatalogueItem = {
  mid: string;
  type?: CatalogueItemType;
  info?: {
    longName?: string;
    shortName?: string;
    skuCode?: string;
    imageDataId?: string | null;
  };
  priceInfo?: { optionSetIds?: string[] };
  variantValues?: { name?: string; value?: string }[];
};

/** A catalogue category as the option sources receive it. */
export type CatalogueCategory = {
  mid: string;
  name?: string;
  imageDataId?: string | null;
  subCategories?: CatalogueCategory[];
};

/** A sale channel as the option sources receive it. */
export type SaleChannel = { mid: string; name?: string };

/** A printer as the option sources receive it. */
export type LabelPrinter = {
  mid: string;
  label?: string;
  /** The string `"true"`, not a boolean — the device writes it as text. */
  enableLabelPrinter?: string;
};

/** An empty settings object, for a branch that has never configured the screen. */
export const NO_SETTINGS: ItemLabelSettings = Object.freeze({});

/** An empty group draft, for the "add new" form. */
export const NEW_GROUP: ItemLabelGroup = Object.freeze({
  [LabelGroupKey.Name]: "",
  [LabelGroupKey.Categories]: [],
  [LabelGroupKey.Printers]: [],
});
